import { Cable, MapPin, Search, User } from 'lucide-react'
import { useEffect, useId, useRef, useState } from 'react'
import { api, type SearchResult } from '../api'

const ICON = { node: MapPin, cable: Cable, service: User }

/** Accessible combobox: type, arrow through results, Enter to open, Escape to close. */
export function SearchBox({ onPick }: { onPick: (r: SearchResult) => void }) {
  const [q, setQ] = useState('')
  const [results, setResults] = useState<SearchResult[]>([])
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const [status, setStatus] = useState('')
  const listId = useId()
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const term = q.trim()
    if (term.length < 2) {
      setResults([])
      setStatus('')
      return
    }
    let cancelled = false
    const t = setTimeout(async () => {
      try {
        const r = await api.search(term)
        if (cancelled) return
        setResults(r.results)
        setActive(0)
        setOpen(true)
        setStatus(r.results.length ? `${r.results.length} results` : 'No matches')
      } catch {
        if (!cancelled) setStatus('Search failed')
      }
    }, 180)
    return () => {
      cancelled = true
      clearTimeout(t)
    }
  }, [q])

  // "/" focuses search from anywhere, like most operator tools.
  useEffect(() => {
    const on = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName
      if (e.key === '/' && tag !== 'INPUT' && tag !== 'TEXTAREA' && tag !== 'SELECT') {
        e.preventDefault()
        inputRef.current?.focus()
      }
    }
    window.addEventListener('keydown', on)
    return () => window.removeEventListener('keydown', on)
  }, [])

  const pick = (r: SearchResult) => {
    onPick(r)
    setOpen(false)
    setQ('')
    inputRef.current?.blur()
  }

  return (
    <div className="relative w-full max-w-md">
      <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" aria-hidden />
      <input
        ref={inputRef}
        role="combobox"
        aria-expanded={open && results.length > 0}
        aria-controls={listId}
        aria-activedescendant={open && results[active] ? `${listId}-${active}` : undefined}
        aria-autocomplete="list"
        aria-label="Search sites, cables and subscribers"
        placeholder="Search NAP, cable, account…  ( / )"
        className="h-9 w-full rounded-lg border border-border bg-surface-2 pl-8 pr-2 text-sm placeholder:text-muted"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => results.length && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault()
            setActive((a) => Math.min(a + 1, results.length - 1))
          } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            setActive((a) => Math.max(a - 1, 0))
          } else if (e.key === 'Enter' && results[active]) {
            e.preventDefault()
            pick(results[active]!)
          } else if (e.key === 'Escape') {
            setOpen(false)
          }
        }}
      />
      <span className="sr-only" aria-live="polite">
        {status}
      </span>
      {open && results.length > 0 && (
        <ul id={listId} role="listbox" className="absolute left-0 right-0 top-10 z-50 max-h-80 overflow-y-auto rounded-lg border border-border bg-surface py-1 shadow-panel">
          {results.map((r, i) => {
            const Icon = ICON[r.type]
            return (
              <li
                key={`${r.type}:${r.id}`}
                id={`${listId}-${i}`}
                role="option"
                aria-selected={i === active}
                onMouseDown={(e) => {
                  e.preventDefault()
                  pick(r)
                }}
                onMouseEnter={() => setActive(i)}
                className={`flex cursor-pointer items-center gap-2 px-3 py-2 text-sm ${i === active ? 'bg-accent-soft' : ''}`}
              >
                <Icon className="h-4 w-4 shrink-0 text-muted" aria-hidden />
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium">{r.label}</span>
                  <span className="block truncate text-xs text-muted">{r.sublabel}</span>
                </span>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
